import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import './infographic-pie.scss';
import { percentageFormatter, useRandomColor } from 'azshared';

interface InfographicItem {
  value: number;
  label?: string;
  color?: string;
  paragraphTitle?: string;
  paragraphText?: string;
}

interface InfographicPieProps {
  pieRadius?: number;
  title?: string;
  subtitle?: string;
  items: InfographicItem[];
  forcedIndex?: number;
  biggerActiveItem?: boolean;
  legend?: boolean;
  legendWidth?: number;
  legendAlignment?: 'right' | 'left';
  lightTooltip?: boolean;
  customStyle?: any;
}

const pieConfig = {
  activeColor: '#007AFF',
  activeTextColor: '#5C5C5C',
  inactiveColor: '#ADBEC9',
  inactiveTextColor: '#ADBEC9'
};

const defaultColors = ['#2E6CD1', '#ADBEC9', '#14376F', '#DEE7EF', '#579C8D', '#372D76', '#7E2E92'];

/**
 * Wraps text by words to a specified maximum length, handling long words.
 */
function wrapTextByWords(str: string, maxLen: number): string[] {
  if (!str) return [];
  const lines: string[] = [];
  const words = str.split(' ');
  let currentLine = '';

  words.forEach((word) => {
    const potentialLine = currentLine ? `${currentLine} ${word}` : word;
    if (potentialLine.length > maxLen) {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
      while (currentLine.length > maxLen) {
        lines.push(currentLine.substring(0, maxLen));
        currentLine = currentLine.substring(maxLen);
      }
    } else {
      currentLine = potentialLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Disegna un popup multilinea dietro al testo.
 * - x, y: coordinate base
 * - labelText: testo completo
 * - textAnchor: 'end' / 'start' / 'middle'
 * - fontSize: dimensione testo px
 * - maxLineLength: # di char per riga
 */
function renderPopupText(x: number, y: number, labelText: string, textAnchor: 'inherit' | 'end' | 'start' | 'middle', fontSize: number, maxLineLength = 30) {
  // Spezza in righe
  const lines = wrapTextByWords(labelText, maxLineLength);

  // Altezza riga
  const lineHeight = Math.round(fontSize * 1.2);

  // Calcoliamo la larghezza massima
  let rectWidth = 0;
  lines.forEach((line) => {
    const w = line.length * fontSize * 0.6; // stima
    if (w > rectWidth) rectWidth = w;
  });

  // Aggiungiamo un po' di padding per sicurezza
  rectWidth += fontSize;

  // Altezza in base al numero di righe + padding
  const rectHeight = lines.length * lineHeight + 8;

  let rectX = x;
  if (textAnchor === 'end') {
    rectX = x - rectWidth;
  } else if (textAnchor === 'middle') {
    rectX = x - rectWidth / 2;
  }
  const rectY = y - (lines.length * lineHeight + 8) / 2 + ((lines.length - 1) * lineHeight) / 2;

  return (
    <>
      {/* Rettangolo scuro trasparente */}
      <rect x={rectX} y={rectY} width={rectWidth} height={rectHeight} fill='rgba(0,0,0,0.5)' rx={4} />
      {/* Testo multilinea con <tspan> */}
      <text x={x} y={y} textAnchor={textAnchor} fontSize={fontSize} fill='#FFF' fontWeight={600} dominantBaseline='middle'>
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            // la prima riga dy=0, le successive scendono di lineHeight
            dy={i === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </>
  );
}

function InfographicPie(props: InfographicPieProps) {
  const {
    pieRadius = 80,
    items,
    biggerActiveItem = false,
    legend = false,
    legendWidth = 220,
    title = '',
    subtitle = '',
    legendAlignment = 'right',
    lightTooltip = false,
    customStyle = {}
  } = props;

  // Indice attivo
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Per calcolare lo zoom in base alla larghezza schermo
  const [pageWidth, setPageWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setPageWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const zoomRatio = useMemo(() => {
    if (pageWidth < 400) return 0.5;
    if (pageWidth < 768) return 0.75;
    return 1;
  }, [pageWidth]);

  const isMobile = useMemo(() => pageWidth <= 600, [pageWidth]);

  const { generateColor } = useRandomColor();

  // Scegli colore predefinito o generato
  const getColor = useCallback(
    (index: number) => {
      if (index >= defaultColors.length) return generateColor();
      return defaultColors[index];
    },
    [generateColor]
  );

  // Applica il colore a ogni item
  const usedColors = useMemo(() => items.map((item, i) => item.color || getColor(i)), [items, getColor]);

  /**
   * useEffect che sposta la fetta attiva
   * (activeIndex) in fondo al DOM, così non è coperta
   */
  useEffect(() => {
    if (activeIndex !== null) {
      const container = document.querySelectorAll('.infographic-pie svg');
      if (!container) return;
      container.forEach((c) => {
        const pie = c.querySelector('.recharts-pie');
        if (!pie) return;
        const activeSections = pie.querySelectorAll('.recharts-pie-sector');

        activeSections.forEach((item) => {
          // Se questa slice ha .recharts-active-shape interna,
          // la spostiamo in fondo al parent.
          const active = item.querySelector('.recharts-active-shape');
          if (active) {
            item.parentNode?.appendChild(item);
          }
        });
      });
    }
  }, [activeIndex]);

  // Per calcolare la posizione del titolo
  const xPieTitle = useMemo(() => {
    if (isMobile) return '50%';
    if (!legend) return '50%';
    if (legendAlignment === 'right') return '38.5%';
    if (biggerActiveItem) return '68%';
    return '64.5%';
  }, [legend, legendAlignment, biggerActiveItem, isMobile]);

  // Forma di base: linea a sinistra + popup multilinea
  const normalShape = useCallback(
    (props: any) => {
      const RADIAN = Math.PI / 180;
      const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, value, payload } = props;

      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);

      // coordinate line
      const sx = cx + outerRadius * cos;
      const sy = cy + outerRadius * sin;
      const mx = cx + outerRadius * cos;
      const my = cy + (outerRadius + 30) * sin;

      const ex = mx - 80; // spostiamo a sinistra
      const ey = my;
      const textAnchor = 'middle';
      const sliceLabel = payload?.label || '';
      const labelText = lightTooltip ? percentageFormatter(value) : `${sliceLabel !== '' ? sliceLabel + ': ' : ''}${percentageFormatter(value)}`;

      // font size
      const fontSize = Math.round(14 * zoomRatio);

      return (
        <>
          <g className='recharts-active-shape'>
            {/* fetta base */}
            <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
            {/* linea */}
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={pieConfig.activeTextColor} fill='none' />
            {/* popup multilinea */}
            {renderPopupText(ex, ey + 18, labelText, textAnchor, fontSize, 30)}
          </g>
        </>
      );
    },
    [zoomRatio, lightTooltip]
  );

  // Forma ingrandita
  const biggerShape = useCallback(
    (props: any) => {
      const RADIAN = Math.PI / 180;
      const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, value, payload } = props;

      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);

      const sx = cx + outerRadius * cos;
      const sy = cy + outerRadius * sin;
      const mx = cx + outerRadius * cos;
      const my = cy + (outerRadius + 10) * sin;
      const ex = mx - 20;
      const ey = my;
      const textAnchor = 'middle';
      const sliceLabel = payload?.label || '';
      const labelText = lightTooltip ? percentageFormatter(value) : `${sliceLabel !== '' ? sliceLabel + ': ' : ''}${percentageFormatter(value)}`;

      const fontSize = Math.round(14 * zoomRatio);

      return (
        <g className='recharts-active-shape'>
          {/* fetta base */}
          <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
          {/* fetta espansa */}
          <Sector cx={cx} cy={cy} startAngle={startAngle} endAngle={endAngle} innerRadius={innerRadius - 5} outerRadius={outerRadius + 15} fill={fill} />

          {/* linea */}
          <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={pieConfig.activeTextColor} fill='none' />

          {/* popup multilinea */}
          {renderPopupText(ex, ey + 18, labelText, textAnchor, fontSize, 30)}
        </g>
      );
    },
    [zoomRatio, lightTooltip]
  );

  const height = useMemo(() => {
    if (isMobile) return items.length < 6 ? 500 : items.length * 70 + 250;
    if (items.length < 6) return 400;
    return items.length * 60;
  }, [items, isMobile]);

  const getOnMouseEnter = useCallback((_: any, idx: number) => {
    setActiveIndex(idx);
  }, []);

  const getOnMouseLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const disableTitle = useMemo(() => {
    return title === '';
  }, [title]);

  return (
    <div className='infographic-pie' style={customStyle}>
      <ResponsiveContainer width={'100%'} height={height}>
        <PieChart height={height} margin={isMobile ? { top: 0, right: 0, left: 0, bottom: 0 } : { top: 20, right: 20, left: 20, bottom: 20 }}>
          {legend && (
            <Legend
              layout='vertical'
              verticalAlign={isMobile ? 'top' : 'top'}
              align={isMobile ? 'center' : legendAlignment}
              wrapperStyle={{
                width: isMobile ? 'auto' : legendWidth + (disableTitle ? 25 : 0),
                maxWidth: isMobile ? '100%' : legendWidth + (disableTitle ? 25 : 0),
                minWidth: isMobile ? 'auto' : legendWidth + (disableTitle ? 25 : 0),
                whiteSpace: isMobile ? 'nowrap' : 'break-spaces',
                marginTop: isMobile ? 20 : 20,
                marginBottom: isMobile ? 0 : 0,
                marginLeft: isMobile ? 'auto' : 0,
                marginRight: isMobile ? 'auto' : 0,
                left: 0,
                zIndex: 0,
                maxHeight: isMobile ? 'auto' : height,
                display: isMobile ? 'block' : 'block'
              }}
            />
          )}
          <Pie
            data={items}
            dataKey='value'
            nameKey='label'
            cx={isMobile ? '50%' : disableTitle ? '60%' : '50%'}
            cy={isMobile ? '60%' : height > 400 ? 250 : '50%'}
            innerRadius={isMobile ? (pieRadius + 10) * zoomRatio : (pieRadius - 20) * zoomRatio}
            outerRadius={isMobile ? (pieRadius + 60) * zoomRatio : (pieRadius + 40) * zoomRatio}
            activeIndex={activeIndex as number}
            activeShape={biggerActiveItem ? biggerShape : normalShape}
            onMouseEnter={getOnMouseEnter}
            onMouseLeave={getOnMouseLeave}>
            {items.map((_, i) => (
              <Cell key={`cell-${i}`} fill={usedColors[i]} />
            ))}
          </Pie>

          {/* Titoli */}
          <g id='pieTitle'>
            <text
              className='pie-title'
              x={xPieTitle}
              y={`${isMobile ? 48 : 58 - (subtitle ? 5.5 : 2.3) * 2 * zoomRatio}%`}
              textAnchor='middle'
              key='title'
              fill='#434548'
              fontSize={isMobile ? 16 : 16 * zoomRatio}
              fontWeight={500}>
              {title}
            </text>
            <text
              className='pie-title'
              x={xPieTitle}
              y={`${isMobile ? 52 : 54 - 0.5 * 2 * zoomRatio}%`}
              textAnchor='middle'
              fill='#434548'
              fontSize={isMobile ? 12 : 12 * zoomRatio}
              fontWeight={500}>
              {subtitle}
            </text>
          </g>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default InfographicPie;
